import React from 'react'
import { Provider } from 'react-redux'
import { Badge, Box, IconButton, Switch, Tooltip, Typography } from '@mui/material'
import LayersIcon from '@mui/icons-material/Layers'
import BaseMapControl from './BaseMapControl'

class LegendToggleControl extends BaseMapControl {
  constructor(store, theme, legends = [], onToggle = null) {
    super({ className: 'legend-toggle-control' })
    this.store = store
    this.theme = theme
    this.legends = legends
    this.onToggle = onToggle
  }

  onAdd(map) {
    const container = super.onAdd(map)
    container.style.overflow = 'visible'
    container.style.position = 'relative'
    return container
  }

  setLegends(legends = []) {
    this.legends = legends
    this._renderContent()
  }

  updateOnToggle(onToggle) {
    this.onToggle = onToggle
    this._renderContent()
  }

  _renderContent() {
    const self = this

    this._root?.render(
      <Provider store={this.store}>
        <LegendToggleButton
          legends={this.legends}
          theme={this.theme}
          onToggle={(legendId, visible) => {
            self.onToggle?.(legendId, visible)
          }}
        />
      </Provider>,
    )
  }
}

const LegendToggleButton = ({ legends, theme, onToggle }) => {
  const [open, setOpen] = React.useState(false)
  const rootRef = React.useRef(null)
  const safeLegends = Array.isArray(legends) ? legends : []
  const visibleCount = safeLegends.filter(legend => legend.visible).length
  const disabled = safeLegends.length === 0

  React.useEffect(() => {
    if (!open) return undefined

    const handleOutsideClick = event => {
      const root = rootRef.current
      if (!root) return

      const path = typeof event.composedPath === 'function'
        ? event.composedPath()
        : []

      if (root.contains(event.target) || path.includes(root)) return
      setOpen(false)
    }

    const handleEscape = event => {
      if (event.key === 'Escape') setOpen(false)
    }

    document.addEventListener('mousedown', handleOutsideClick, true)
    document.addEventListener('touchstart', handleOutsideClick, true)
    document.addEventListener('keydown', handleEscape, true)

    return () => {
      document.removeEventListener('mousedown', handleOutsideClick, true)
      document.removeEventListener('touchstart', handleOutsideClick, true)
      document.removeEventListener('keydown', handleEscape, true)
    }
  }, [open])

  const buttonStyles = {
    minWidth: '30px',
    width: '30px',
    height: '30px',
    padding: 0,
    margin: 0,
    color: open || visibleCount
      ? theme.palette.primary.main
      : theme.palette.text.primary,
    bgcolor: open || visibleCount
      ? theme.palette.secondary.main
      : theme.palette.background.paper,
    border: open ? `2px solid ${theme.palette.primary.main}` : 'none',
    boxShadow: open ? '0 0 8px rgba(37, 99, 235, 0.35)' : 'none',
    '&:hover': {
      bgcolor: theme.palette.secondary.main,
      color: theme.palette.primary.main,
    },
    '&.Mui-disabled': {
      bgcolor: theme.palette.background.paper,
      color: theme.palette.text.disabled,
    },
  }

  const panelStyles = {
    position: 'absolute',
    top: 0,
    right: 38,
    width: 282,
    maxWidth: 'calc(100vw - 76px)',
    p: 1.1,
    borderRadius: 1.5,
    bgcolor: 'rgba(255, 255, 255, 0.88)',
    border: '1px solid rgba(15, 23, 42, 0.12)',
    boxShadow: '0 10px 28px rgba(15, 23, 42, 0.2)',
    backdropFilter: 'blur(10px)',
    color: theme.palette.text.primary,
  }

  return (
    <Box ref={rootRef} sx={{ position: 'relative', width: 30, height: 30 }}>
      <Tooltip title="Leyendas del mapa" placement="left">
        <span>
          <Badge
            color="primary"
            variant="dot"
            invisible={visibleCount === 0}
            overlap="circular"
            sx={{
              '& .MuiBadge-badge': {
                right: 5,
                top: 5,
              },
            }}>
            <IconButton
              size="small"
              disabled={disabled}
              onClick={() => setOpen(current => !current)}
              sx={buttonStyles}
              aria-label="Mostrar opciones de leyendas"
              aria-expanded={open ? 'true' : 'false'}>
              <LayersIcon fontSize="small" />
            </IconButton>
          </Badge>
        </span>
      </Tooltip>

      {open && (
        <Box sx={panelStyles}>
          <Typography
            sx={{
              px: 0.75,
              pb: 0.75,
              fontSize: '0.72rem',
              fontWeight: 800,
              textTransform: 'uppercase',
              letterSpacing: 0,
              color: 'rgba(15, 23, 42, 0.72)',
            }}>
            Leyendas del mapa
          </Typography>

          <Box sx={{ display: 'grid', gap: 0.75, maxHeight: 320, overflowY: 'auto' }}>
            {safeLegends.map(legend => {
              const isVisible = Boolean(legend.visible)
              const tooltip = isVisible
                ? `Ocultar ${legend.label}`
                : `Mostrar ${legend.label}`

              return (
                <Box
                  key={legend.id}
                  component="div"
                  role="button"
                  tabIndex={legend.disabled ? -1 : 0}
                  aria-pressed={isVisible}
                  onClick={() => {
                    if (!legend.disabled) onToggle(legend.id, !isVisible)
                  }}
                  onKeyDown={event => {
                    if (legend.disabled) return
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault()
                      onToggle(legend.id, !isVisible)
                    }
                  }}
                  title={tooltip}
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: '1fr auto',
                    alignItems: 'center',
                    gap: 1.25,
                    minHeight: 46,
                    px: 1.15,
                    py: 0.75,
                    borderRadius: 1,
                    cursor: legend.disabled ? 'not-allowed' : 'pointer',
                    opacity: legend.disabled ? 0.55 : 1,
                    color: isVisible
                      ? theme.palette.primary.main
                      : theme.palette.text.primary,
                    bgcolor: isVisible
                      ? 'rgba(37, 99, 235, 0.09)'
                      : 'rgba(248, 250, 252, 0.74)',
                    border: isVisible
                      ? `1px solid ${theme.palette.primary.main}`
                      : '1px solid rgba(15, 23, 42, 0.10)',
                    '&:hover': {
                      bgcolor: isVisible
                        ? 'rgba(37, 99, 235, 0.14)'
                        : 'rgba(226, 232, 240, 0.9)',
                    },
                    '&:focus-visible': {
                      outline: `2px solid ${theme.palette.primary.main}`,
                      outlineOffset: 2,
                    },
                  }}>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography
                      sx={{
                        fontSize: '0.82rem',
                        fontWeight: 800,
                        lineHeight: 1.18,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}>
                      {legend.label}
                    </Typography>
                    <Typography
                      sx={{
                        mt: 0.2,
                        fontSize: '0.68rem',
                        fontWeight: 700,
                        lineHeight: 1.15,
                        color: isVisible
                          ? 'rgba(37, 99, 235, 0.74)'
                          : 'rgba(15, 23, 42, 0.54)',
                      }}>
                      {isVisible ? 'Visible en pantalla' : 'Oculta'}
                    </Typography>
                  </Box>
                  <Switch
                    size="small"
                    checked={isVisible}
                    disabled={legend.disabled}
                    onClick={event => {
                      event.stopPropagation()
                    }}
                    onChange={event => {
                      event.stopPropagation()
                      onToggle(legend.id, event.target.checked)
                    }}
                    sx={{
                      mr: -0.6,
                      '& .MuiSwitch-switchBase.Mui-checked': {
                        color: theme.palette.primary.main,
                      },
                      '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                        backgroundColor: theme.palette.primary.main,
                      },
                    }}
                    inputProps={{ 'aria-label': tooltip }}
                  />
                </Box>
              )
            })}
          </Box>
        </Box>
      )}
    </Box>
  )
}

export default LegendToggleControl
